<template>
    <div class="prospective-patent-analysis-prior-art d-flex flex-column w-full h-full">
        <p class="w-100 bold">U.S. Patent Prior Art Results</p>
        <p class="w-100">
            We analyze U.S. patent documents using semantic similarity and keyword techniques to
            identify potentially relevant prior art. Each result includes a ranked patent based on
            relevancy with a brief justification—and can be clicked to view full patent details in
            the patent viewer.
        </p>
        <div class="display-flex w-100">
            <AonButton
                class="table-button"
                :class="{ 'active-table-button': activeSideBarType === 'columns' }"
                label="Columns"
                text-with-icon
                type="subtle"
                :icon-options="{ iconStyle: 'fas', iconName: 'fa-objects-column' }"
                @clicked="() => toggleSideBar('columns', gridApi)"
            />
            <AonButton
                class="ml-md"
                label="Export Table"
                text-with-icon
                :icon-options="{ iconStyle: 'fas', iconName: 'fa-download' }"
                @clicked="exportTable"
            />
            <div
                class="clear-filters display-flex align-items-center cursor-pointer p-sm ml-sm"
                @click="clearFilters"
            >
                <font-awesome-icon :icon="`fas fa-xmark`" class="text-moat-600" />
                <p class="geist-font text-moat-600 bold ml-xs">Clear Filters</p>
            </div>
        </div>
        <MoatTable
            style="width: 100%; height: 600px"
            :class="themeClass"
            :column-defs="patentDomainColDefs"
            :row-data="patentDomainData"
            :loading="priorArtLoading"
            :overlay-no-rows-template="noRowsTemplate"
            :side-bar="sideBar"
            @cell-clicked="onRowClicked"
            @cell-key-down="onCellKeyDown"
            @grid-ready="gridInit"
        />
        <p class="w-100 bold">Public Web & Academic Article Prior Art Results</p>
        <p class="w-100">
            We search publicly available websites and scholarly sources (e.g., academic articles,
            technical blogs) to uncover non-patent prior art. Each result includes a source link and
            a justification describing its relevance to your disclosure.
        </p>
        <MoatTable
            style="width: 100%; height: 600px"
            :class="themeClass"
            :column-defs="nonPatentDomainColDefs"
            :row-data="nonPatentDomainData"
            :loading="priorArtLoading"
            :overlay-no-rows-template="noRowsTemplate"
        />

        <Teleport to="#aon-app .main-content">
            <PatentDetails v-if="patentViewerStore.patentDetailsOpen" />
        </Teleport>
    </div>
</template>

<script setup>
import { computed, ref, inject, onBeforeUnmount, onMounted, onUnmounted } from 'vue'
import { useProspectivePatentPlaybookStore } from '@/stores/prospectivePatentPlaybook.js'
import { usePatentViewerStore } from '@/stores'

import { AgGridVue as MoatTable } from '@ag-grid-community/vue3'
import MTCustomCellTooltip from '@/components/moatTable/MTCustomCellTooltip.vue'
import MTCustomCellLink from '@/components/moatTable/MTCustomCellLink.vue'
import MTCustomCellRelevance from '@/components/moatTable/MTCustomCellRelevance.vue'
import { useAgGridSidebar } from '@/composables/useAgGridSidebar.js'
import PatentDetails from '@/components/patentViewer/PatentDetails.vue'

import { debounce } from 'lodash-es'

const playbookStore = useProspectivePatentPlaybookStore()
const patentViewerStore = usePatentViewerStore()
const eventBus = inject('eventBus')
const themeClass = ref('ag-theme-quartz')

const gridApi = ref(null)
const selectedPatentIndex = ref(0)
const {
    sideBar,
    activeSideBarType,
    toggleSideBar,
    injectSidebarHeaders,
    clearFilters: sidebarClearFilters,
} = useAgGridSidebar({
    panels: [
        {
            id: 'columns',
            label: 'Columns',
            width: 275,
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressRowGroups: true,
                suppressValues: true,
                suppressPivotMode: true,
            },
        },
    ],
    injectHeaders: {
        columns: 'Columns',
    },
    position: 'left',
})

patentViewerStore.setHideMappedProducts(true)

const priorArtLoading = computed(() => playbookStore.loadingStates?.priorArt?.loading ?? true)
const priorArt = computed(() => playbookStore.priorArt)
const errorMsg = playbookStore.loadingStates?.priorArt?.error
const tableData = computed(() => {
    return priorArt.value?.searchResults ?? []
})
const patentDomainData = computed(() =>
    tableData.value.filter((row) => row.source === 'similarity' || row.source === 'googlePatents')
)

const nonPatentDomainData = computed(() =>
    tableData.value.filter((row) => row.source !== 'similarity' && row.source !== 'googlePatents')
)

const noRowsTemplate = computed(() => {
    return `<span class="ag-overlay-no-rows-text">${errorMsg ?? 'No Prior Art Found'}</span>`
})

onMounted(() => {
    debounceManager.init()
    eventBus.on('next-patent', async () => {
        const nextRow = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value + 1)
        if (nextRow?.data?.patentId) {
            gridApi.value.deselectAll()
            gridApi.value.setFocusedCell(selectedPatentIndex.value + 1, 'patentId')
            await processRowSelection(nextRow.data.publicationPk, nextRow.data)

            selectedPatentIndex.value++
        }
    })
    eventBus.on('previous-patent', async () => {
        const previousRow = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value - 1)
        if (previousRow?.data?.patentId) {
            gridApi.value.deselectAll()
            gridApi.value.setFocusedCell(selectedPatentIndex.value - 1, 'patentId')
            await processRowSelection(previousRow.data.publicationPk, previousRow.data)
            selectedPatentIndex.value--
        }
    })

    eventBus.on('close-patent', () => {
        patentViewerStore.setTargetPatentId(null)
    })
})

onBeforeUnmount(() => {
    patentViewerStore.resetState()
    eventBus.off('next-patent')
    eventBus.off('previous-patent')
    eventBus.off('close-patent')
    debounceManager.cancelAll()
})

const gridInit = (params) => {
    gridApi.value = params.api
    injectSidebarHeaders()
}

const patentDomainColDefs = [
    {
        field: 'source',
        headerName: 'Source',
        valueFormatter: (p) => {
            if (p.data) {
                if (p.data.source === 'similarity') {
                    return 'Semantic Similarity'
                } else {
                    return 'Keyword'
                }
            }
        },
    },
    {
        field: 'relevanceScore',
        headerName: 'Relevance',
        sort: 'desc',
        sortIndex: 0,
        cellRenderer: MTCustomCellRelevance,
    },
    { field: 'patentId', headerName: 'Patent ID' },
    {
        field: 'justification',
        headerName: 'Justification',
        flex: 2,
        cellRenderer: MTCustomCellTooltip,
    },
]

const nonPatentDomainColDefs = [
    {
        field: 'source',
        headerName: 'Source',
        valueFormatter: (p) => {
            if (p.data) {
                if (p.data.source === 'google_scholar') {
                    return 'Academic Articles'
                } else {
                    return 'Web Search'
                }
            }
        },
    },
    {
        field: 'relevanceScore',
        headerName: 'Relevance',
        sort: 'desc',
        sortIndex: 0,
        cellRenderer: MTCustomCellRelevance,
    },
    { field: 'link', headerName: 'Link', cellClass: 'link', cellRenderer: MTCustomCellLink },
    {
        field: 'justification',
        headerName: 'Justification',
        flex: 2,
        cellRenderer: MTCustomCellTooltip,
    },
]

const onRowClicked = async (params) => {
    selectedPatentIndex.value = params.rowIndex
    if (params?.data?.patentId) {
        await processRowSelection(params.data.publicationPk, params.data)
    }
}

const onCellKeyDown = async (params) => {
    selectedPatentIndex.value = params.rowIndex
    let newPatentId = null
    let row = null
    if (params.event.key === 'ArrowUp') {
        row = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value - 1)
        if (row?.data?.patentId) {
            newPatentId = row.data.publicationPk
        }
    } else if (params.event.key === 'ArrowDown') {
        row = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value + 1)
        if (row?.data?.patentId) {
            newPatentId = row.data.publicationPk
        }
    }
    await processRowSelection(newPatentId, row.data)
}

const processRowSelection = async (patentId, rowData) => {
    if (patentViewerStore.patentExistsInCache(patentId)) {
        await patentViewerStore.setTargetPatent(patentId)
    } else {
        rowData.isRowData = true
        await patentViewerStore.setTargetPatentId(patentId)
        updateTargetPatentData(patentId, rowData)
    }

    eventBus.emit('patent-changed', patentId)
}

const debounceManager = {
    debounceTime: 500,
    patentUpdate: null,
    gridRefresh: null,

    init() {
        this.patentUpdate = debounce(
            (patentId, rowData, callback) => callback(patentId, rowData),
            this.debounceTime
        )
        this.gridRefresh = debounce((callback) => callback(), this.debounceTime)
    },

    cancelAll() {
        this.patentUpdate.cancel()
        this.gridRefresh.cancel()
    },
}

const updateTargetPatentData = (patentId, rowData) => {
    if (patentId) {
        debounceManager.patentUpdate(patentId, rowData, async (id, data) => {
            patentViewerStore.setCachedPatentRowDetail(id, data)
            await patentViewerStore.fetchPatentDetails(id)
        })
    }
}

const exportTable = () => {
    gridApi.value.exportDataAsExcel()
}

const clearFilters = () => {
    if (gridApi.value) {
        sidebarClearFilters(gridApi.value)
    }
}

onUnmounted(() => {
    patentViewerStore.setHideMappedProducts(false)
})
</script>

<style scoped lang="scss">
.prospective-patent-analysis-prior-art {
    justify-content: start;
    align-items: center;
    gap: 20px;
}
</style>
